import { BaseMarker } from '../base/baseMarker'
import { BezierLineMarkerStateManager } from './bezierLineMarkerStateManager'
import { BezierLineMarkerController } from './bezierLineMarkerController'
import { BezierLineMarkerRenderer } from './bezierLineMarkerRenderer'
import { BezierLineMarkerHandles } from './bezierLineMarkerHandles'
import { isCollinear } from '../../helperFunctions'
import { ControlPoint, Point } from '../../types'

class BezierLineMarker extends BaseMarker<
  BezierLineMarkerStateManager,
  BezierLineMarkerRenderer,
  BezierLineMarkerController,
  BezierLineMarkerHandles
> {
  constructor() {
    super()
    this.controller = new BezierLineMarkerController(this)
    this.renderer = new BezierLineMarkerRenderer(this)
    this.state = new BezierLineMarkerStateManager(this)
    this.handles = new BezierLineMarkerHandles(this)
  }

  public static typeName = 'BezierLineMarker'

  getMouseToShapeDistance(): number {
    const state = this.state
    //Can't draw line with less than two points
    const minimumPointsDefined = state.anchorPoints.length > 1
    //There's always one less control point than anchor point
    const controlPointsDefined = state.anchorPoints.length - 1 === state.controlPoints.length

    if (!minimumPointsDefined || !controlPointsDefined) return Infinity
    const anchorPoints = state.anchorPoints
    const controlPoints = state.controlPoints
    const mouseX = this.controller.mouseX
    const mouseY = this.controller.mouseY
    const dist = this.p.dist

    let minDistance = Infinity

    for (let i = 0; i < anchorPoints.length - 1; i++) {
      for (let t = 0; t <= 1; t += 0.001) {
        const bezierCurvePoint = this.calculateBezierPoint(anchorPoints[i], anchorPoints[i + 1], controlPoints[i], t)

        const d = dist(mouseX, mouseY, bezierCurvePoint.x, bezierCurvePoint.y)
        if (d < minDistance) {
          minDistance = d
        }
      }
    }
    return minDistance
  }

  calculateBezierPoint(startAnchorPoint: Point, endAnchorPoint, controlPoint: ControlPoint, position: number): Point {
    const bezierPoint = this.p.bezierPoint
    const x = bezierPoint(
      startAnchorPoint.x,
      controlPoint.start?.x || startAnchorPoint.x,
      controlPoint.end.x,
      endAnchorPoint.x,
      position
    )
    const y = bezierPoint(
      startAnchorPoint.y,
      controlPoint.start.y || startAnchorPoint.y,
      controlPoint.end.y,
      endAnchorPoint.y,
      position
    )
    return {
      x: x,
      y: y,
    }
  }

  isMousedOver(): boolean {
    const state = this.state
    //Can't draw line with less than two points
    const minimumPointsDefined = state.anchorPoints.length > 1
    //There's always one less control point than anchor point
    const controlPointsDefined = state.anchorPoints.length - 1 === state.controlPoints.length

    if (!minimumPointsDefined || !controlPointsDefined) return false
    // Tolerance is the maximum distance from the curve to consider as 'over'
    const tolerance = 5
    const mouseToCurveDistance = this.getMouseToShapeDistance()
    const handleMousedOver = this.handles.getMousedOverHandle() !== undefined

    const isOver = mouseToCurveDistance < tolerance
    return isOver || handleMousedOver
  }

  approximateBezierCurve() {
    const state = this.state
    const anchorPoints = state.anchorPoints
    const controlPoints = state.controlPoints
    const vertices = []
    const tolerance = 0.01 // Interval for sampling points
    const bezierPoint = this.p.bezierPoint

    for (let i = 0; i < anchorPoints.length - 1; i++) {
      let lastVertex = anchorPoints[i]
      vertices.push({ x: lastVertex.x, y: lastVertex.y }) // Always include the first point

      //TODO Pull out common functionaltiy shared with isMousedOver
      for (let t = 0; t <= 1; t += tolerance) {
        const x = bezierPoint(
          anchorPoints[i].x,
          controlPoints[i].start?.x || anchorPoints[i].x,
          controlPoints[i].end.x,
          anchorPoints[i + 1].x,
          t
        )
        const y = bezierPoint(
          anchorPoints[i].y,
          controlPoints[i].start?.y || anchorPoints[i].y,
          controlPoints[i].end.y,
          anchorPoints[i + 1].y,
          t
        )

        const currentVertex = { x, y }

        if (isCollinear(lastVertex, currentVertex, vertices[vertices.length - 1])) {
          vertices[vertices.length - 1] = currentVertex // Replace the last vertex
        } else {
          vertices.push(currentVertex)
          lastVertex = currentVertex
        }
      }

      // Always include the last point of the segment
      const lastAnchor = state.anchorPoints[i + 1]
      vertices.push({ x: lastAnchor.x, y: lastAnchor.y })
    }

    return vertices
  }
}

export { BezierLineMarker }
